import React, { useRef, useState, useEffect, useCallback } from "react";
import "./Pictures.css";

const Picture = () => {
    const trackRef = useRef(null);
    const nestedContainerRef = useRef(null);

    const [photosData, setPhotosData] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [currentImage, setCurrentImage] = useState(null);
    const [showTrack, setShowTrack] = useState(true);

    const [visibleImages, setVisibleImages] = useState([]);
    const [fullscreenImage, setFullscreenImage] = useState(null);

    // full nested images cache
    const [allNestedImages, setAllNestedImages] = useState([]);

    // ✅ download naming sequence
    const [downloadCounts, setDownloadCounts] = useState({});

    const isMobile = window.innerWidth <= 768;

    const openFullscreen = (img) => {
        setFullscreenImage(img);
    };

    const closeFullscreen = () => {
        setFullscreenImage(null);
    };

    // ✅ DOWNLOAD
    const handleDownload = async (url) => {

        const response = await fetch(url, {
            mode: "cors"
        });

        const blob = await response.blob();

        const blobUrl =
            window.URL.createObjectURL(blob);

        const baseName =
            (selectedCategory?.name || "image")
                .toLowerCase()
                .replace(/\s+/g, "-");

        const currentCount =
            downloadCounts[baseName] || 1;

        const fileName =
            `${baseName}-${currentCount}.jpg`;

        const link =
            document.createElement("a");

        link.href = blobUrl;
        link.download = fileName;

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);

        setDownloadCounts((prev) => ({
            ...prev,
            [baseName]: currentCount + 1,
        }));
    };

    // ✅ ONLY MAIN IMAGES FETCH
    useEffect(() => {

        fetch(
            `${process.env.REACT_APP_API_URL}/api/v1/getallImage`
        )
            .then((res) => res.json())
            .then((result) => {

                const data =
                    (result.responseData || [])
                        .sort(
                            (a, b) =>
                                (a.order || 0) -
                                (b.order || 0)
                        );

                setPhotosData(
                    data.map((item) => ({
                        id: item._id,
                        order: item.order,
                        mainImages:
                            item.mainImages || [],
                    }))
                );
            })
            .catch((err) =>
                console.error(err)
            );

    }, []);

    // ✅ MAIN IMAGES WITH POST ID
    const mainImages = photosData.flatMap(
        (item) =>
            (item.mainImages || []).map(
                (img) => ({
                    ...img,
                    postId: item.id,
                })
            )
    );

    const smoothScrollTo = (
        container,
        target,
        duration = 500
    ) => {

        const start =
            container.scrollLeft;

        const change =
            target - start;

        const startTime =
            performance.now();

        const animateScroll = (
            currentTime
        ) => {

            const elapsed =
                currentTime - startTime;

            const progress = Math.min(
                elapsed / duration,
                1
            );

            const easeInOut =
                progress < 0.5
                    ? 2 *
                    progress *
                    progress
                    : -1 +
                    (4 - 2 * progress) *
                    progress;

            container.scrollLeft =
                start +
                change * easeInOut;

            if (elapsed < duration) {
                requestAnimationFrame(
                    animateScroll
                );
            }
        };

        requestAnimationFrame(
            animateScroll
        );
    };

    // ✅ LOAD MORE ON SCROLL
    const loadMoreImages = useCallback(() => {

        if (!allNestedImages.length)
            return;

        setVisibleImages((prev) => {

            const next =
                allNestedImages.slice(
                    0,
                    prev.length + 10
                );

            return next;
        });

    }, [allNestedImages]);

    // ✅ SCROLL DETECTION
    useEffect(() => {

        const container =
            nestedContainerRef.current;

        if (!container) return;

        const handleScroll = () => {

            const nearEnd =
                container.scrollLeft +
                container.clientWidth >=
                container.scrollWidth - 300;

            if (nearEnd) {
                loadMoreImages();
            }
        };

        container.addEventListener(
            "scroll",
            handleScroll
        );

        return () => {

            container.removeEventListener(
                "scroll",
                handleScroll
            );
        };

    }, [allNestedImages, loadMoreImages]);

    // ✅ FETCH NESTED IMAGES ONLY WHEN CLICKED
    const handleImageClick = async (
        mainImage,
        postId
    ) => {

        try {

            const response =
                await fetch(
                    `${process.env.REACT_APP_API_URL}/api/v1/getNestedImages/${postId}`
                );

            const result =
                await response.json();

            const nestedMap =
                result.responseData
                    ?.nestedImagesMap;

            if (!nestedMap) return;

            let foundCategory = null;

            Object.values(
                nestedMap
            ).forEach((group) => {

                if (
                    group.name ===
                    mainImage.caption
                ) {

                    foundCategory = {
                        name: group.name,

                        images:
                            group.images.map(
                                (img) => img.url
                            ),
                    };
                }
            });

            if (!foundCategory)
                return;

            setSelectedCategory(
                foundCategory
            );

            setCurrentImage(
                foundCategory.images[0]
            );

            setShowTrack(false);

            // SAVE ALL
            setAllNestedImages(
                foundCategory.images
            );

            // LOAD ONLY FIRST 10
            setVisibleImages(
                foundCategory.images.slice(
                    0,
                    10
                )
            );

            setTimeout(() => {

                if (
                    nestedContainerRef.current
                ) {

                    const container =
                        nestedContainerRef.current;

                    const firstRealImg =
                        container.children[2];

                    if (firstRealImg) {

                        const containerCenter =
                            container.offsetWidth / 2;

                        const imgCenter =
                            firstRealImg.offsetLeft +
                            firstRealImg.offsetWidth /
                            2;

                        const scrollLeft =
                            imgCenter -
                            containerCenter;

                        smoothScrollTo(
                            container,
                            scrollLeft,
                            500
                        );
                    }
                }

            }, 50);

        } catch (error) {

            console.log(error);
        }
    };

    const handleBackClick = () => {

        setSelectedCategory(null);

        setCurrentImage(null);

        setVisibleImages([]);

        setAllNestedImages([]);

        setShowTrack(true);
    };

    return (
        <div>

            {showTrack ? (

                <div className="image-track-wrapper">

                    <div
                        id="image-track"
                        ref={trackRef}
                    >

                        {mainImages.map(
                            (imgObj, idx) => (

                                <div
                                    key={idx}
                                    className="image-container"
                                    style={{
                                        display:
                                            "inline-block",
                                        textAlign:
                                            "center",
                                        cursor:
                                            "pointer",
                                    }}
                                    onClick={() =>
                                        handleImageClick(
                                            imgObj,
                                            imgObj.postId
                                        )
                                    }
                                >

                                    <img
                                        className="image"
                                        src={imgObj.src}
                                        loading="lazy"
                                        draggable="false"
                                        alt=""
                                        style={{
                                            width:
                                                isMobile
                                                    ? "65vw"
                                                    : "40vmin",
                                            height:
                                                isMobile
                                                    ? "90vw"
                                                    : "56vmin",
                                            objectFit:
                                                "cover",
                                            borderRadius:
                                                "10px",
                                            boxShadow:
                                                "0 4px 15px rgba(0,0,0,0.3)",
                                        }}
                                    />

                                    <p
                                        style={{
                                            marginTop:
                                                "0.5rem",
                                            fontSize:
                                                isMobile
                                                    ? "1rem"
                                                    : "1.2rem",
                                            color:
                                                "#333",
                                        }}
                                    >
                                        {
                                            imgObj.caption
                                        }
                                    </p>

                                </div>
                            )
                        )}

                    </div>

                </div>

            ) : (

                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        height: "100vh",
                        overflow: "hidden",
                    }}
                >

                    <button
                        onClick={
                            handleBackClick
                        }
                        style={{
                            position: "absolute",
                            top: "2%",
                            right: "1rem",
                            padding:
                                isMobile
                                    ? "0.5rem 1rem"
                                    : "0.6rem 1.4rem",
                            fontSize:
                                isMobile
                                    ? "0.9rem"
                                    : "1rem",
                            borderRadius: "20px",
                            border: "none",
                            background: "#fff",
                            color: "#000",
                            fontWeight: "600",
                            boxShadow:
                                "0 4px 15px rgba(0,0,0,0.25)",
                            cursor: "pointer",
                            zIndex: 100,
                        }}
                    >
                        ← Back
                    </button>

                    <div
                        ref={
                            nestedContainerRef
                        }
                        style={{
                            display: "flex",
                            alignItems:
                                isMobile
                                    ? "flex-start"
                                    : "center",
                            justifyContent:
                                "flex-start",
                            gap:
                                isMobile
                                    ? "0.5rem"
                                    : "1rem",
                            height: "100%",
                            overflowX: "auto",
                            paddingTop:
                                isMobile
                                    ? "25vh"
                                    : "1.5rem",
                            paddingBottom:
                                isMobile
                                    ? "6vh"
                                    : "0rem",
                        }}
                    >

                        {[
                            { isDummy: true },
                            { isDummy: true },

                            ...visibleImages.map(
                                (src) => ({
                                    src,
                                    isDummy: false,
                                })
                            ),

                            { isDummy: true },
                            { isDummy: true },

                        ].map(
                            (imgObj, idx) => {

                                if (
                                    imgObj.isDummy
                                ) {

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                width:
                                                    isMobile
                                                        ? "120px"
                                                        : "400px",
                                                flex:
                                                    "0 0 auto",
                                            }}
                                        />
                                    );
                                }

                                const isSelected =
                                    imgObj.src ===
                                    currentImage;

                                return (

                                    <div
                                        key={idx}
                                        style={{
                                            position:
                                                "relative",
                                            flex:
                                                "0 0 auto",
                                            display:
                                                "flex",
                                            flexDirection:
                                                "column",
                                            alignItems:
                                                "center",
                                        }}
                                    >

                                        <img
                                            src={
                                                imgObj.src
                                            }
                                            loading="lazy"
                                            alt=""
                                            draggable="false"
                                            onClick={() => {

                                                setCurrentImage(
                                                    imgObj.src
                                                );

                                                if (
                                                    nestedContainerRef.current
                                                ) {

                                                    const container =
                                                        nestedContainerRef.current;

                                                    const img =
                                                        container.children[idx];

                                                    const containerCenter =
                                                        container.offsetWidth /
                                                        2;

                                                    const imgCenter =
                                                        img.offsetLeft +
                                                        img.offsetWidth /
                                                        2;

                                                    const scrollLeft =
                                                        imgCenter -
                                                        containerCenter;

                                                    smoothScrollTo(
                                                        container,
                                                        scrollLeft,
                                                        500
                                                    );
                                                }
                                            }}
                                            style={{
                                                width:
                                                    isMobile
                                                        ? "240px"
                                                        : "400px",
                                                height:
                                                    isMobile
                                                        ? "240px"
                                                        : "400px",
                                                objectFit:
                                                    "cover",
                                                borderRadius:
                                                    "8px",
                                                cursor:
                                                    "pointer",
                                                transform:
                                                    `scale(${isSelected
                                                        ? isMobile
                                                            ? 1.05
                                                            : 1.2
                                                        : 0.85
                                                    })`,
                                                opacity:
                                                    isSelected
                                                        ? 1
                                                        : 0.5,
                                                transition:
                                                    "0.3s",
                                            }}
                                        />
                                        {isSelected && (

                                            <div
                                                style={{
                                                    position: "absolute",
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    top: isMobile
                                                        ? "-75px"
                                                        : "-115px",
                                                    display: "flex",
                                                    gap: isMobile
                                                        ? "14px"
                                                        : "18px",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    zIndex: 10,
                                                }}
                                            >

                                                {/* DOWNLOAD BUTTON */}
                                                <button
                                                    onClick={() =>
                                                        handleDownload(
                                                            imgObj.src
                                                        )
                                                    }
                                                    style={{
                                                        width: isMobile
                                                            ? "52px"
                                                            : "48px",
                                                        height: isMobile
                                                            ? "52px"
                                                            : "48px",
                                                        borderRadius: "50%",
                                                        border: "none",
                                                        background:
                                                            "rgba(255,255,255,0.95)",
                                                        backdropFilter:
                                                            "blur(10px)",
                                                        WebkitBackdropFilter:
                                                            "blur(10px)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        cursor: "pointer",
                                                        boxShadow:
                                                            "0 8px 25px rgba(0,0,0,0.18)",
                                                        transition:
                                                            "all 0.25s ease",
                                                        color: "#222",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform =
                                                            "scale(1.12)";
                                                        e.currentTarget.style.boxShadow =
                                                            "0 12px 30px rgba(0,0,0,0.25)";
                                                        e.currentTarget.style.background =
                                                            "#ffffff";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform =
                                                            "scale(1)";
                                                        e.currentTarget.style.boxShadow =
                                                            "0 8px 25px rgba(0,0,0,0.18)";
                                                        e.currentTarget.style.background =
                                                            "rgba(255,255,255,0.95)";
                                                    }}
                                                    title="Download"
                                                >

                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={isMobile ? "22" : "20"}
                                                        height={isMobile ? "22" : "20"}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path d="M12 3v12" />
                                                        <path d="M7 10l5 5 5-5" />
                                                        <path d="M5 21h14" />
                                                    </svg>

                                                </button>

                                                {/* FULLSCREEN BUTTON */}
                                                <button
                                                    onClick={() =>
                                                        openFullscreen(
                                                            imgObj.src
                                                        )
                                                    }
                                                    style={{
                                                        width: isMobile
                                                            ? "52px"
                                                            : "48px",
                                                        height: isMobile
                                                            ? "52px"
                                                            : "48px",
                                                        borderRadius: "50%",
                                                        border: "none",
                                                        background:
                                                            "rgba(255,255,255,0.95)",
                                                        backdropFilter:
                                                            "blur(10px)",
                                                        WebkitBackdropFilter:
                                                            "blur(10px)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        cursor: "pointer",
                                                        boxShadow:
                                                            "0 8px 25px rgba(0,0,0,0.18)",
                                                        transition:
                                                            "all 0.25s ease",
                                                        color: "#222",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform =
                                                            "scale(1.12)";
                                                        e.currentTarget.style.boxShadow =
                                                            "0 12px 30px rgba(0,0,0,0.25)";
                                                        e.currentTarget.style.background =
                                                            "#ffffff";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform =
                                                            "scale(1)";
                                                        e.currentTarget.style.boxShadow =
                                                            "0 8px 25px rgba(0,0,0,0.18)";
                                                        e.currentTarget.style.background =
                                                            "rgba(255,255,255,0.95)";
                                                    }}
                                                    title="Fullscreen"
                                                >

                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={isMobile ? "22" : "20"}
                                                        height={isMobile ? "22" : "20"}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <polyline points="15 3 21 3 21 9" />
                                                        <polyline points="9 21 3 21 3 15" />
                                                        <line
                                                            x1="21"
                                                            y1="3"
                                                            x2="14"
                                                            y2="10"
                                                        />
                                                        <line
                                                            x1="3"
                                                            y1="21"
                                                            x2="10"
                                                            y2="14"
                                                        />
                                                    </svg>

                                                </button>

                                            </div>
                                        )}

                                    </div>
                                );
                            }
                        )}

                    </div>

                </div>
            )}

            {fullscreenImage && (

                <div
                    onClick={
                        closeFullscreen
                    }
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background:
                            "rgba(0,0,0,0.9)",
                        display: "flex",
                        alignItems:
                            "center",
                        justifyContent:
                            "center",
                        zIndex: 9999,
                    }}
                >

                    <img
                        src={fullscreenImage}
                        alt=""
                        style={{
                            maxWidth: "95vw",
                            maxHeight: "95vh",
                            objectFit:
                                "contain",
                            borderRadius:
                                "10px",
                        }}
                    />

                    <button
                        onClick={
                            closeFullscreen
                        }
                        style={{
                            position: "absolute",
                            top: "20px",
                            right: "20px",
                            background: "#fff",
                            border: "none",
                            padding: "10px 15px",
                            borderRadius: "50%",
                            cursor: "pointer",
                            fontSize: "16px",
                            color: "#000",
                            fontWeight: "700",
                            lineHeight: "1",
                        }}
                    >
                        ✕
                    </button>

                </div>
            )}

        </div>
    );
};

export default Picture;